import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "warning", "warningText"]
  static values = { min: Number, max: Number, type: String }
  
  connect() {
    console.log("🎯 OMS Global spread controller connected!", {
      min: this.minValue,
      max: this.maxValue,
      type: this.typeValue,
      element: this.element
    })
    
    // Add a visible indicator that the controller is working
    this.element.style.border = "1px dashed blue"
    setTimeout(() => {
      this.element.style.border = ""
    }, 2000)
  }
  
  apply(event) {
    event.preventDefault()
    
    const spreadValue = parseFloat(this.inputTarget.value)
    
    if (isNaN(spreadValue)) {
      this.showWarning("Please enter a valid spread value")
      return
    }
    
    // Validate against limits
    if (this.hasMinValue && spreadValue < this.minValue) {
      this.showWarning(`Spread must be at least ${this.minValue}`)
      return
    }
    
    if (this.hasMaxValue && spreadValue > this.maxValue) {
      this.showWarning(`Spread cannot exceed ${this.maxValue}`)
      return
    }
    
    this.hideWarning()
    this.applyToAllLineItems(spreadValue)
  }
  
  applyToAllLineItems(spreadValue) {
    console.log("Applying target spread to all line items:", spreadValue)
    
    // Find all quote line items
    const lineItems = document.querySelectorAll('.quote-line-item:not([style*="display: none"])')
    
    console.log("Found line items:", lineItems.length)
    
    if (lineItems.length === 0) {
      this.showWarning("No securities found to apply spread to")
      return
    }
    
    let appliedCount = 0
    let skippedCount = 0
    
    lineItems.forEach((lineItem, index) => {
      // Get inventory margin for this line item
      const inventoryMarginField = lineItem.querySelector('input[name*="[inventory_margin]"]')
      const marketMarginField = lineItem.querySelector('input[name*="[market_margin]"]')
      
      // Use inventory margin if available, otherwise fall back to market margin
      let baseMargin = null
      if (inventoryMarginField && inventoryMarginField.value) {
        baseMargin = parseFloat(inventoryMarginField.value) * 100 // Convert to percentage
      } else if (marketMarginField && marketMarginField.value) {
        baseMargin = parseFloat(marketMarginField.value) // Already in percentage
      }
      
      if (baseMargin === null || isNaN(baseMargin)) {
        console.log(`Skipping line item ${index}: no inventory or market margin`)
        skippedCount++
        return
      }
      
      const transactionType = lineItem.querySelector('select[name*="[transaction_type]"]')?.value || 'buy'
      
      // Calculate target margin based on spread and transaction type
      let targetMargin
      if (transactionType === 'buy') {
        targetMargin = baseMargin + spreadValue
      } else {
        targetMargin = baseMargin - spreadValue
      }
      
      // Find the margin input and spread input
      const marginInput = lineItem.querySelector('.margin-input')
      const spreadInput = lineItem.querySelector('.spread-input')
      
      if (marginInput && spreadInput) {
        const marginSource = inventoryMarginField && inventoryMarginField.value ? 'inventory' : 'market'
        console.log(`Line item ${index}: Setting margin to ${targetMargin.toFixed(4)} for spread ${spreadValue} (using ${marginSource} margin)`)
        
        // Set the spread value
        spreadInput.value = spreadValue.toFixed(4)
        spreadInput.dataset.cleared = 'true'
        
        // Set the calculated margin
        marginInput.value = targetMargin.toFixed(4)
        marginInput.dataset.cleared = 'true'
        
        // Clear price and yield fields
        const priceInput = lineItem.querySelector('.price-input')
        const yieldInput = lineItem.querySelector('.yield-input')
        if (priceInput) {
          priceInput.value = ''
          priceInput.removeAttribute('data-cleared')
        }
        if (yieldInput) {
          yieldInput.value = ''
          yieldInput.removeAttribute('data-cleared')
        }
        
        // Trigger input event to update UI state
        marginInput.dispatchEvent(new Event('input', { bubbles: true }))
        spreadInput.dispatchEvent(new Event('input', { bubbles: true }))
        
        appliedCount++
      }
    })
    
    // Dispatch custom event for quote securities controller to handle
    this.dispatch("spread-applied", { 
      detail: { 
        spread: spreadValue,
        type: this.typeValue 
      } 
    })
    
    // Show success feedback
    if (appliedCount > 0) {
      const message = skippedCount > 0 
        ? `Target spread of ${spreadValue.toFixed(2)}${this.getSpreadUnit()} applied to ${appliedCount} securities (${skippedCount} skipped - no inventory or market margin)`
        : `Target spread of ${spreadValue.toFixed(2)}${this.getSpreadUnit()} applied to all ${appliedCount} securities`
      this.showSuccess(message)
    } else {
      this.showWarning("Could not apply spread - no securities have inventory or market margin data")
    }
  }
  
  showWarning(message) {
    this.warningTextTarget.textContent = message
    this.warningTarget.classList.remove("hidden")
  }
  
  hideWarning() {
    this.warningTarget.classList.add("hidden")
  }
  
  showSuccess(message) {
    // Temporarily show success message in warning area with different styling
    this.warningTextTarget.textContent = message
    this.warningTarget.classList.remove("hidden", "text-amber-600")
    this.warningTarget.classList.add("text-success-600")
    
    setTimeout(() => {
      this.hideWarning()
      this.warningTarget.classList.remove("text-success-600")
      this.warningTarget.classList.add("text-amber-600")
    }, 3000)
  }
  
  getSpreadTypeLabel() {
    return this.typeValue === 'capital-price-premium' ? 'Capital Price Premium' : 'Margin Spread'
  }
  
  getSpreadUnit() {
    return this.typeValue === 'capital-price-premium' ? '$' : '%'
  }
}
